import { useState, useEffect } from "react"
import Header from "../common/Header.js"
import Sidebar from "../common/Sidebar.js"
import dashboardService from "../../services/DashboardService.js"
import Alert from "../common/Alert.js"
import {
  FileText, Image, TrendingUp, Filter, X,
  ClipboardCheck, Package, Receipt, CheckCircle2,
  Clock, BarChart3, ShieldCheck, Activity, Download, LayoutDashboard
} from "lucide-react"
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts'

const COLORS = ['#F97316', '#3B82F6', '#10B981', '#F59E0B', '#14B8A6']

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow-xl p-3 text-sm">
        <p className="font-semibold text-gray-700 mb-2">{label}</p>
        {payload.map((p, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ background: p.color }} />
            <span className="text-gray-600">{p.name}:</span>
            <span className="font-bold text-gray-900">{p.value}</span>
          </div>
        ))}
      </div>
    )
  }
  return null
}

const StatCard = ({ title, value, subtitle, icon: Icon, gradient, textLight }) => (
  <div className={`${gradient} rounded-md shadow-lg p-5 py-3 text-white relative overflow-hidden`}>
    <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-white opacity-5 -translate-y-8 translate-x-8" />
    <div className="relative">
      <div className="flex items-center justify-between mb-4">
        <p className={`${textLight} text-sm font-medium`}>{title}</p>
        <div className="bg-white bg-opacity-20 p-2.5 rounded-xl">
          <Icon size={22} />
        </div>
      </div>
      <h3 className="text-3xl font-bold">{value}</h3>
      {subtitle && <p className={`${textLight} text-xs mt-1`}>{subtitle}</p>}
    </div>
  </div>
)

const SectionHeader = ({ icon: Icon, title, color = "text-orange" }) => (
  <div className="flex items-center gap-2 mb-4 bg-gray-100 p-2 px-1">
    <div className="w-1 h-6 rounded-full bg-orange-500" />
    <Icon size={20} className={color} />
    <h2 className="text-lg font-semibold text-gray-600">{title}</h2>
  </div>
)

const DocumentationManagerDashboard = () => {
  const [loading, setLoading] = useState(true)
  const [dashboardData, setDashboardData] = useState(null)
  const [filters, setFilters] = useState({ from_date: '', to_date: '' })
  const [showFilters, setShowFilters] = useState(false)
  const [datePreset, setDatePreset] = useState('')

  useEffect(() => {
    const userRole = dashboardService.getCurrentUserRole()
    const userId = dashboardService.getCurrentUserId()
    console.log('[v0] DocumentationManagerDashboard initialized - Role:', userRole, '- User ID:', userId)
    
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async (customFilters = filters) => {
    try {
      setLoading(true)
      const response = await dashboardService.getDashboardData(customFilters)
      setDashboardData(response.data)
    } catch (err) {
      Alert.error('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }))
    setDatePreset('')
  }

  const handleDatePresetChange = (preset) => {
    if (!preset) { clearFilters(); return }
    const presets = dashboardService.getDatePresets()
    const selected = presets[preset]
    if (selected) {
      const newFilters = { from_date: selected.from, to_date: selected.to }
      setFilters(newFilters)
      setDatePreset(preset)
      fetchDashboardData(newFilters)
    }
  }

  const applyFilters = () => { fetchDashboardData(); setShowFilters(false) }

  const clearFilters = () => {
    const cleared = { from_date: '', to_date: '' }
    setFilters(cleared)
    setDatePreset('')
    fetchDashboardData(cleared)
  }

  const exportTIReport = () => {
    const ti = dashboardData?.ti_report
    if (!ti) return
    const csv = [
      ['Metric', 'Value'],
      ['Total TI Reports', ti.total],
      ['Published / Done', ti.published],
      ['Pending Review', ti.unpublished],
    ].map(r => r.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a'); a.href = url; a.download = 'ti_report_summary.csv'; a.click()
    URL.revokeObjectURL(url)
  }

  const hasActiveFilters = filters.from_date || filters.to_date

  // ─── Chart helpers ─────────────────────────────────────────────────────
  const prepareAreaChartData = () => {
    if (!dashboardData?.records_by_date) return []
    const dateMap = new Map()
    Object.entries(dashboardData.records_by_date).forEach(([key, data]) => {
      data.forEach(item => {
        if (!dateMap.has(item.date)) dateMap.set(item.date, { date: item.date })
        const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        dateMap.get(item.date)[label] = item.count
      })
    })
    return Array.from(dateMap.values()).sort((a, b) => new Date(a.date) - new Date(b.date))
  }

  const prepareWorkloadChart = () => {
    if (!dashboardData) return []
    return [
      { name: 'Dry Box', processed: dashboardData.dry_box_survey?.published || 0, pending: dashboardData.dry_box_survey?.unpublished || 0 },
      { name: 'Tank Clean', processed: dashboardData.tank_cleaning?.published || 0, pending: dashboardData.tank_cleaning?.unpublished || 0 },
      { name: 'On Hire', processed: dashboardData.onhire?.published || 0, pending: dashboardData.onhire?.unpublished || 0 },
      { name: 'EI Receipt', processed: dashboardData.ei_receipt?.published || 0, pending: dashboardData.ei_receipt?.unpublished || 0 },
      { name: 'TI Report', processed: dashboardData.ti_report?.published || 0, pending: dashboardData.ti_report?.unpublished || 0 },
    ]
  }

  const prepareRecordTypesChart = () => {
    if (!dashboardData) return []
    return [
      { name: 'Dry Box', value: dashboardData.dry_box_survey?.total || 0 },
      { name: 'Tank Clean', value: dashboardData.tank_cleaning?.total || 0 },
      { name: 'On Hire', value: dashboardData.onhire?.total || 0 },
      { name: 'EI Receipt', value: dashboardData.ei_receipt?.total || 0 },
      { name: 'TI Report', value: dashboardData.ti_report?.total || 0 },
    ]
  }

  const prepareTITrendData = () => {
    if (!dashboardData?.records_by_date?.ti_report) return []
    return dashboardData.records_by_date.ti_report.map(item => ({ date: item.date, reports: item.count }))
  }

  const prepareTIStatusData = () => {
    const ti = dashboardData?.ti_report
    if (!ti) return []
    return [
      { name: 'Published', value: ti.published, fill: '#10B981' },
      { name: 'Pending', value: ti.unpublished, fill: '#F59E0B' },
    ]
  }

  const totalRecords = (dashboardData?.dry_box_survey?.total || 0) + (dashboardData?.tank_cleaning?.total || 0) +
    (dashboardData?.onhire?.total || 0) + (dashboardData?.ei_receipt?.total || 0) + (dashboardData?.ti_report?.total || 0)

  const totalProcessed = (dashboardData?.dry_box_survey?.published || 0) + (dashboardData?.tank_cleaning?.published || 0) +
    (dashboardData?.onhire?.published || 0) + (dashboardData?.ei_receipt?.published || 0) + (dashboardData?.ti_report?.published || 0)

  const totalPending = totalRecords - totalProcessed
  const ti = dashboardData?.ti_report || {}

  if (loading && !dashboardData) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-orange-500 mx-auto mb-4" />
          <p className="text-gray-500 font-medium">Loading dashboard…</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-gray-50 w-full">
          <div className="flex-1 flex flex-col">
            <main className="flex-1 p-6 overflow-auto">

              {/* ── Page header ── */}
              <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
                <div>
                  <h1 className="text-2xl sm:text-2xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                    <LayoutDashboard className="text-orange" size={28} />
                    Documentation Manager Dashboard
                  </h1>
                </div>
                <button onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all ${
                    hasActiveFilters ? 'bg-orange-500 text-white shadow-md' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}>
                  <Filter size={16} />
                  Filters
                  {hasActiveFilters && <span className="bg-white text-orange-500 px-1.5 py-0.5 rounded-full text-xs font-bold">Active</span>}
                </button>
              </div>

              {/* ── Filter panel ── */}
              {showFilters && (
                <div className="bg-white rounded-md shadow-sm border border-gray-100 p-6 mb-8">
                  <div className="flex items-center justify-between mb-5">
                    <h3 className="font-bold text-gray-800">Date Range Filter</h3>
                    <button onClick={() => setShowFilters(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                    {[
                      { label: 'Date Preset', el: (
                        <select value={datePreset} onChange={e => handleDatePresetChange(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500">
                          <option value="">Custom Range</option>
                          {['today','yesterday','last7Days','last30Days','last3Months','thisMonth','thisYear'].map(k => (
                            <option key={k} value={k}>{dashboardService.getDatePresets()[k]?.label}</option>
                          ))}
                        </select>
                      )},
                      { label: 'From Date', el: <input type="date" value={filters.from_date} onChange={e => handleFilterChange('from_date', e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500" /> },
                      { label: 'To Date', el: <input type="date" value={filters.to_date} onChange={e => handleFilterChange('to_date', e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500" /> },
                    ].map(({ label, el }) => (
                      <div key={label}>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{label}</label>
                        {el}
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-3 justify-end">
                    <button onClick={clearFilters} className="px-4 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm hover:bg-gray-50">Clear</button>
                    <button onClick={applyFilters} className="px-4 py-2 bg-buttonBg text-white rounded-xl text-sm hover:bg-orange-600 font-medium">Apply Filters</button>
                  </div>
                </div>
              )}

              {/* ══════════════════════════════════════
                  SECTION 1 – WORKLOAD SUMMARY
              ══════════════════════════════════════ */}
              <SectionHeader icon={Activity} title="Workload Summary" />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
                <StatCard title="Total Records" value={totalRecords} subtitle="All document types" icon={FileText} gradient="bg-gradient-to-br from-blue-500 to-blue-600" textLight="text-blue-100" />
                <StatCard title="Processed" value={totalProcessed} subtitle="Published / done" icon={CheckCircle2} gradient="bg-gradient-to-br from-green-500 to-green-600" textLight="text-green-100" />
                <StatCard title="Pending Review" value={totalPending} subtitle="Awaiting action" icon={Clock} gradient="bg-gradient-to-br from-yellow-500 to-yellow-600" textLight="text-yellow-100" />
                <StatCard title="Total Images" value={dashboardData?.file_cabinet_images?.total || 0} subtitle="Managed files" icon={Image} gradient="bg-gradient-to-br from-purple-500 to-purple-600" textLight="text-purple-100" />
              </div>

              {/* Record type cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
                {[
                  { label: 'Dry Box Survey', data: dashboardData?.dry_box_survey, icon: FileText, color: 'text-orange-500', border: 'border-orange-500' },
                  { label: 'Tank Cleaning', data: dashboardData?.tank_cleaning, icon: ClipboardCheck, color: 'text-blue-500', border: 'border-blue-500' },
                  { label: 'On Hire', data: dashboardData?.onhire, icon: Package, color: 'text-green-500', border: 'border-green-500' },
                  { label: 'EI Receipt', data: dashboardData?.ei_receipt, icon: Receipt, color: 'text-purple-500', border: 'border-purple-500' },
                  { label: 'TI Report', data: dashboardData?.ti_report, icon: ShieldCheck, color: 'text-teal-500', border: 'border-teal-500' },
                ].map(({ label, data, icon: Icon, color, border }) => (
                  <div key={label} className={`bg-white rounded-md shadow-sm border border-gray-100 p-5 border-l-4 ${border}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <Icon size={16} className={color} />
                      <h4 className="font-semibold text-gray-700 text-xs">{label}</h4>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{data?.total || 0}</p>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full">✓ {data?.published || 0} Done</span>
                      <span className="text-xs bg-yellow-50 text-yellow-700 px-2 py-0.5 rounded-full">⧗ {data?.unpublished || 0}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Charts Row 1 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                <div className="bg-white rounded-md shadow-sm border border-gray-100 p-6">
                  <div className="flex items-center gap-2 mb-5">
                    <TrendingUp size={18} className="text-orange" />
                    <h3 className="font-semibold text-gray-600">Documentation Activity Over Time</h3>
                  </div>
                  <ResponsiveContainer width="100%" height={260}>
                    <AreaChart data={prepareAreaChartData()} margin={{ top: 5, right: 10, left: 0, bottom: 60 }}>
                      <defs>
                        {[['dry','#F97316'],['tank','#3B82F6'],['hire','#10B981'],['ei','#8B5CF6'],['ti','#14B8A6']].map(([k,c]) => (
                          <linearGradient key={k} id={`dmg${k}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={c} stopOpacity={0.15}/><stop offset="95%" stopColor={c} stopOpacity={0}/>
                          </linearGradient>
                        ))}
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} angle={-40} textAnchor="end" height={70} tickLine={false} />
                      <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend iconType="circle" iconSize={8} />
                      <Area type="monotone" dataKey="Dry Box Survey" stroke="#F97316" strokeWidth={2} fill="url(#dmgdry)" dot={false} />
                      <Area type="monotone" dataKey="Tank Cleaning" stroke="#3B82F6" strokeWidth={2} fill="url(#dmgtank)" dot={false} />
                      <Area type="monotone" dataKey="Onhire" stroke="#10B981" strokeWidth={2} fill="url(#dmghire)" dot={false} />
                      <Area type="monotone" dataKey="Ei Receipt" stroke="#8B5CF6" strokeWidth={2} fill="url(#dmgei)" dot={false} />
                      <Area type="monotone" dataKey="Ti Report" stroke="#14B8A6" strokeWidth={2} fill="url(#dmgti)" dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-white rounded-md shadow-sm border border-gray-100 p-6">
                  <div className="flex items-center gap-2 mb-5">
                    <BarChart3 size={18} className="text-orange" />
                    <h3 className="font-semibold text-gray-600">Workload Status by Type</h3>
                  </div>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={prepareWorkloadChart()} barCategoryGap="30%">
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} tickLine={false} />
                      <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend iconType="circle" iconSize={8} />
                      <Bar dataKey="processed" fill="#10B981" name="Processed" radius={[4,4,0,0]} />
                      <Bar dataKey="pending" fill="#F59E0B" name="Pending" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                <div className="bg-white rounded-md shadow-sm border border-gray-100 p-6">
                  <h3 className="font-semibold text-gray-600 mb-5">Records by Type Distribution</h3>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={prepareRecordTypesChart()} cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={3} dataKey="value">
                        {prepareRecordTypesChart().map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="grid grid-cols-2 gap-2 mt-4">
                    {prepareRecordTypesChart().map((d, i) => (
                      <div key={d.name} className="flex items-center gap-2 text-sm">
                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: COLORS[i] }} />
                        <span className="text-gray-600 truncate">{d.name}</span>
                        <span className="font-bold text-gray-800 ml-auto">{d.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-md shadow-sm border border-gray-100 p-6">
                  <div className="flex items-center gap-2 mb-5">
                    <Image size={18} className="text-orange" />
                    <h3 className="font-semibold text-gray-600">Managed Images by Category</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: 'Dry Box', val: dashboardData?.file_cabinet_images?.dry_box, bg: 'bg-orange-50', text: 'text-orange-600' },
                      { label: 'Tank Cleaning', val: dashboardData?.file_cabinet_images?.tank_cleaning, bg: 'bg-blue-50', text: 'text-blue-600' },
                      { label: 'On Hire', val: dashboardData?.file_cabinet_images?.onhire, bg: 'bg-green-50', text: 'text-green-600' },
                      { label: 'EI Receipt', val: dashboardData?.file_cabinet_images?.ei_receipt, bg: 'bg-purple-50', text: 'text-purple-600' },
                    ].map(({ label, val, bg, text }) => (
                      <div key={label} className={`text-center p-4 ${bg} rounded-xl`}>
                        <p className={`text-2xl font-bold ${text}`}>{val || 0}</p>
                        <p className="text-xs text-gray-500 mt-1">{label}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 text-center p-3 bg-gray-100 rounded-xl">
                    <p className="text-2xl font-bold text-gray-900">{dashboardData?.file_cabinet_images?.total || 0}</p>
                    <p className="text-xs text-gray-600 font-semibold mt-1">Total Images</p>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DocumentationManagerDashboard
